import { Button, Container } from '@playcanvas/pcui';

import { NumericInput } from '../ui/numeric-input';
import { Entity, Mat4, Quat, TranslateGizmo, Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { Transform } from '../transform';
import { pointerPositionInElement } from './pointer-coordinates';

/** Display value = meters * UNITS[unit] (nm = nautical miles) */
const MEASURE_UNITS = ['mm', 'cm', 'm', 'ft', 'km', 'mi', 'nm'] as const;
type MeasureUnit = (typeof MEASURE_UNITS)[number];

const UNITS_TO_METERS_FACTOR: Record<MeasureUnit, number> = {
    mm: 1000,
    cm: 100,
    m: 1,
    ft: 3.28084,
    km: 0.001,
    mi: 0.000621371,
    nm: 0.000539957
};

function precisionForUnit(unit: MeasureUnit): number {
    switch (unit) {
        case 'mm':
        case 'cm':
        case 'ft':
            return 1;
        case 'm':
            return 2;
        case 'km':
        case 'mi':
        case 'nm':
            return 4;
        default:
            return 2;
    }
}

const mat = new Mat4();
const p = new Vec3();
const p0 = new Vec3();
const p1 = new Vec3();
const screen0 = new Vec3();
const screen1 = new Vec3();

const t = new Transform();

class MeasureTransformHandler {
    activate() {}
    deactivate() {}
}

class MeasureTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, parent: HTMLElement, canvasContainer: Container) {
        // create svg
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.classList.add('tool-svg', 'hidden');
        svg.id = 'measure-tool-svg';
        parent.appendChild(svg);

        const ns = svg.namespaceURI;

        // create defs node
        const defs = document.createElementNS(ns, 'defs');

        // create line element
        const line = document.createElementNS(ns, 'line') as SVGLineElement;
        line.id = 'measure-line';
        defs.appendChild(line);

        const lineBottom = document.createElementNS(ns, 'use') as SVGUseElement;
        lineBottom.id = 'measure-line-bottom';
        lineBottom.setAttribute('href', '#measure-line');

        const lineTop = document.createElementNS(ns, 'use') as SVGUseElement;
        lineTop.id = 'measure-line-top';
        lineTop.setAttribute('href', '#measure-line');

        // create line ends
        const lineStart = document.createElementNS(ns, 'circle') as SVGCircleElement;
        lineStart.id = 'measure-line-start';

        const lineEnd = document.createElementNS(ns, 'circle') as SVGCircleElement;
        lineEnd.id = 'measure-line-end';

        svg.appendChild(defs);
        svg.appendChild(lineBottom);
        svg.appendChild(lineTop);
        svg.appendChild(lineStart);
        svg.appendChild(lineEnd);

        /** Mid-line distance label (Google Maps–style: white box, parallel to segment). */
        const labelG = document.createElementNS(ns, 'g');
        labelG.id = 'measure-distance-label';
        labelG.setAttribute('visibility', 'hidden');
        labelG.setAttribute('pointer-events', 'none');
        const labelBg = document.createElementNS(ns, 'rect');
        labelBg.setAttribute('fill', '#ffffff');
        labelBg.setAttribute('stroke', '#1a1a1a');
        labelBg.setAttribute('stroke-width', '1');
        const labelText = document.createElementNS(ns, 'text') as SVGTextElement;
        labelText.setAttribute('fill', '#1a1a1a');
        labelText.setAttribute('font-family', 'system-ui, -apple-system, Segoe UI, sans-serif');
        labelText.setAttribute('font-size', '12');
        labelText.setAttribute('font-weight', '600');
        labelText.setAttribute('text-anchor', 'middle');
        labelText.setAttribute('dominant-baseline', 'central');
        labelText.setAttribute('x', '0');
        labelText.setAttribute('y', '0');
        labelG.appendChild(labelBg);
        labelG.appendChild(labelText);
        svg.appendChild(labelG);

        // ui — [value] [unit dropdown]; internal length always meters
        const wcfg = (window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: {
                volugraphMeasureDisplayScale?: number;
            };
        }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
        const defaultUnit: MeasureUnit = wcfg?.volugraphMeasureDisplayScale === 1000 ? 'mm' : 'm';
        let currentUnit: MeasureUnit = defaultUnit;

        const lengthInput = new NumericInput({
            width: 90,
            placeholder: '',
            precision: precisionForUnit(currentUnit),
            min: 0.0001,
            value: 0
        });

        let suppressUI = 0;

        /** Custom unit menu (native select list is not styleable — match app currency dropdown rounding) */
        const unitWrap = document.createElement('div');
        unitWrap.className = 'select-toolbar-unit-wrap';

        const unitTrigger = document.createElement('button');
        unitTrigger.type = 'button';
        unitTrigger.className = 'select-toolbar-unit-trigger';
        unitTrigger.setAttribute('aria-haspopup', 'listbox');
        unitTrigger.setAttribute('aria-expanded', 'false');
        unitTrigger.setAttribute('aria-label', 'Distance unit');

        const unitTriggerLabel = document.createElement('span');
        unitTriggerLabel.className = 'select-toolbar-unit-trigger-label';
        const unitChevron = document.createElement('span');
        unitChevron.className = 'select-toolbar-unit-trigger-chevron';
        unitChevron.setAttribute('aria-hidden', 'true');
        unitTrigger.appendChild(unitTriggerLabel);
        unitTrigger.appendChild(unitChevron);

        const unitList = document.createElement('ul');
        unitList.className = 'select-toolbar-unit-list';
        unitList.setAttribute('role', 'listbox');
        unitList.hidden = true;

        const syncUnitMenuUi = () => {
            unitTriggerLabel.textContent = currentUnit;
            unitList.querySelectorAll<HTMLLIElement>('.select-toolbar-unit-option').forEach((li) => {
                const u = li.getAttribute('data-unit') as MeasureUnit;
                li.classList.toggle('is-selected', u === currentUnit);
                li.setAttribute('aria-selected', u === currentUnit ? 'true' : 'false');
            });
        };

        const applyUnitChange = (newUnit: MeasureUnit) => {
            if (newUnit === currentUnit || !UNITS_TO_METERS_FACTOR[newUnit]) return;

            const oldUnit = currentUnit;
            const displayed = typeof lengthInput.value === 'number' ? lengthInput.value : Number(lengthInput.value);
            const meters = Number.isFinite(displayed) ? displayed / UNITS_TO_METERS_FACTOR[oldUnit] : 0;

            currentUnit = newUnit;
            lengthInput.precision = precisionForUnit(currentUnit);
            suppressUI++;
            lengthInput.value = meters * UNITS_TO_METERS_FACTOR[currentUnit];
            suppressUI--;
            syncUnitMenuUi();
        };

        for (const u of MEASURE_UNITS) {
            const li = document.createElement('li');
            li.className = 'select-toolbar-unit-option';
            li.setAttribute('role', 'option');
            li.setAttribute('data-unit', u);
            li.textContent = u;
            li.addEventListener('pointerdown', (e) => e.stopPropagation());
            li.addEventListener('click', (e) => {
                e.stopPropagation();
                applyUnitChange(u);
                closeUnitList();
            });
            unitList.appendChild(li);
        }
        syncUnitMenuUi();

        const onDocPointerDownCloseUnit = (e: PointerEvent) => {
            if (!unitWrap.contains(e.target as Node)) closeUnitList();
        };

        const closeUnitList = () => {
            if (unitList.hidden) return;
            unitList.hidden = true;
            unitTrigger.setAttribute('aria-expanded', 'false');
            document.removeEventListener('pointerdown', onDocPointerDownCloseUnit, true);
        };

        const openUnitList = () => {
            unitList.hidden = false;
            unitTrigger.setAttribute('aria-expanded', 'true');
            window.setTimeout(() => {
                document.addEventListener('pointerdown', onDocPointerDownCloseUnit, true);
            }, 0);
        };

        unitTrigger.addEventListener('pointerdown', (e) => e.stopPropagation());
        unitTrigger.addEventListener('click', (e) => {
            e.stopPropagation();
            if (unitTrigger.disabled) return;
            if (unitList.hidden) openUnitList();
            else closeUnitList();
        });

        unitWrap.appendChild(unitTrigger);
        unitWrap.appendChild(unitList);

        const applyScaleBtn = new Button({
            text: 'APPLY REF. SCALE',
            class: ['select-toolbar-button', 'select-toolbar-button--measure-ref']
        });
        applyScaleBtn.enabled = false;
        applyScaleBtn.dom.setAttribute(
            'title',
            'Commits the current distance correction as this capture’s measurement scale. Does not resize the splat — future measures use this calibration until you change or clear it in the editor (saved to your project).'
        );

        const selectToolbar = new Container({
            class: 'select-toolbar',
            hidden: true
        });

        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        selectToolbar.append(lengthInput);
        selectToolbar.dom.appendChild(unitWrap);
        selectToolbar.append(applyScaleBtn);
        canvasContainer.append(selectToolbar);

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        const entity = new Entity('measureGizmoPivot');
        const transformHandler = new MeasureTransformHandler();

        let active = false;
        let splat: Splat;

        /**
         * Session tweak: displayed = raw × globalMeasureCalibration × referenceScale.
         * APPLY REF. SCALE folds referenceScale into globalMeasureCalibration (no geometry change).
         * Persisted multiplier is restored via `volugraphMeasureCalibrationMultiplier` in embed config and
         * `volugraph.setMeasureCalibrationMultiplier` at runtime.
         */
        const wCfgMeasure = (window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphMeasureCalibrationMultiplier?: number };
        }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
        const initMult = wCfgMeasure?.volugraphMeasureCalibrationMultiplier;
        let globalMeasureCalibration =
            typeof initMult === 'number' && Number.isFinite(initMult) && initMult > 0 ? initMult : 1;
        let referenceScale = 1;

        const getRawMeasuredLength = (): number => {
            if (!splat || splat.measurePoints.length !== 2) return 0;
            getPoint(0, p0);
            getPoint(1, p1);
            return p0.distance(p1);
        };

        const syncApplyScaleBtn = () => {
            const canApply =
                !!splat &&
                splat.measurePoints.length === 2 &&
                lengthInput.enabled &&
                Math.abs(referenceScale - 1) > 1e-6;
            applyScaleBtn.enabled = canApply;
        };

        const removeMeasureConfirmOverlay = () => {
            canvasContainer.dom.querySelectorAll('.measure-confirm-overlay').forEach((el) => el.remove());
        };

        // get world space point
        const getPoint = (index: number, result: Vec3) => {
            splat.worldTransform.transformPoint(splat.measurePoints[index], result);
        };

        const getPoint2d = (index: number, result: Vec3) => {
            getPoint(index, result);
            scene.camera.worldToScreen(result, result);
            const { canvas } = scene;
            result.x *= canvas.clientWidth;
            result.y *= canvas.clientHeight;
        };

        const updateVisuals = () => {
            gizmo.detach();

            if (splat && active && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                getPoint(splat.measureSelection, p);
                t.set(p, Quat.IDENTITY, Vec3.ONE);
                events.invoke('pivot').place(t);
                entity.setLocalPosition(p);
                gizmo.attach(entity);
            }

            if (splat && splat.measurePoints.length === 2) {
                getPoint(0, p0);
                getPoint(1, p1);
                const lenMeters = p0.distance(p1);

                suppressUI++;
                lengthInput.value =
                    lenMeters * globalMeasureCalibration * referenceScale * UNITS_TO_METERS_FACTOR[currentUnit];
                lengthInput.enabled = true;
                suppressUI--;
            } else {
                lengthInput.enabled = false;
                referenceScale = 1;
            }
            unitTrigger.disabled = !lengthInput.enabled;
            if (!lengthInput.enabled) closeUnitList();
            syncApplyScaleBtn();
        };

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        gizmo.on('transform:start', () => {
            events.invoke('pivot').start();
        });

        gizmo.on('transform:move', () => {
            events.invoke('pivot').moveTRS(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
        });

        gizmo.on('transform:end', () => {
            events.invoke('pivot').end();
        });

        events.on('selection.changed', (selection: Splat) => {
            splat = selection;
            if (active) {
                // for now we always deactivate the tool so the current transform handler remains in place
                events.fire('tool.deactivate');
            }
        });

        events.on('pivot.started', () => {

        });

        events.on('pivot.moved', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                const p = events.invoke('pivot').transform.position;
                mat.invert(splat.worldTransform);
                mat.transformPoint(p, splat.measurePoints[splat.measureSelection]);
            }
            scene.forceRender = true;
        });

        events.on('pivot.ended', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                updateVisuals();
            }
        });

        // Typing updates session referenceScale relative to raw geometry and existing global calibration.
        lengthInput.on('change', (value) => {
            if (suppressUI) return;
            const n = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(n)) return;
            const rawLen = getRawMeasuredLength();
            if (rawLen <= 0) return;
            const denom = rawLen * globalMeasureCalibration;
            if (denom <= 0) return;
            const worldLen = n / UNITS_TO_METERS_FACTOR[currentUnit];
            referenceScale = worldLen / denom;
            syncApplyScaleBtn();
        });

        applyScaleBtn.dom.addEventListener('pointerdown', (e) => e.stopPropagation());
        applyScaleBtn.dom.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!applyScaleBtn.enabled) return;
            const rawLen = getRawMeasuredLength();
            if (rawLen <= 0) return;
            globalMeasureCalibration *= referenceScale;
            referenceScale = 1;
            suppressUI++;
            updateVisuals();
            suppressUI--;
            events.fire('volugraph.measureScaleCalibrated', {
                globalCalibration: globalMeasureCalibration
            });
        });

        events.on('volugraph.setMeasureCalibrationMultiplier', (mult: number) => {
            if (typeof mult !== 'number' || !Number.isFinite(mult) || mult <= 0) return;
            globalMeasureCalibration = mult;
            suppressUI++;
            updateVisuals();
            suppressUI--;
        });

        events.removeFunction('volugraph.measureCalibrationEffective');
        events.function('volugraph.measureCalibrationEffective', () => globalMeasureCalibration);

        events.on('select.delete', () => {
            if (active && splat && splat.measureSelection >= 0 && splat.measureSelection < splat.measurePoints.length) {
                splat.measurePoints.splice(splat.measureSelection, 1);
                splat.measureSelection--;
                updateVisuals();
            }
        });

        const isPrimary = (e: PointerEvent) => {
            return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
        };

        let clicked = false;

        const pointerdown = (e: PointerEvent) => {
            if (!clicked && isPrimary(e)) {
                clicked = true;
            }
        };

        const pointermove = (e: PointerEvent) => {
            clicked = false;
        };

        const pointerup = async (e: PointerEvent) => {
            if (splat && clicked && isPrimary(e)) {
                clicked = false;

                let closestIdx = -1;

                // check for intersection with existing point
                const ptr = pointerPositionInElement(e, scene.canvas);

                for (let i = 0; i < splat.measurePoints.length; i++) {
                    getPoint2d(i, p);

                    if (Math.abs(p.x - ptr.x) < 8 && Math.abs(p.y - ptr.y) < 8) {
                        closestIdx = i;
                        break;
                    }
                }

                if (closestIdx >= 0) {
                    splat.measureSelection = closestIdx;
                    updateVisuals();
                    return;
                }

                if (splat.measurePoints.length < 2) {
                    const result = await scene.camera.intersect(ptr.nx, ptr.ny);
                    if (result) {
                        mat.invert(splat.worldTransform);
                        mat.transformPoint(result.position, p);
                        splat.measureSelection = splat.measurePoints.length;
                        splat.measurePoints.push(p.clone());
                        updateVisuals();
                    }
                }

                e.preventDefault();
                e.stopPropagation();
            }
        };

        events.on('postrender', () => {
            if (active && splat) {
                line.setAttribute('visibility', splat.measurePoints.length > 1 ? 'visible' : 'hidden');

                for (let i = 0; i < 2; i++) {
                    if (i < splat.measurePoints.length) {
                        getPoint2d(i, p);

                        const x = p.x.toString();
                        const y = p.y.toString();

                        if (i === 0) {
                            line.setAttribute('x1', x);
                            line.setAttribute('y1', y);
                            lineStart.setAttribute('cx', x);
                            lineStart.setAttribute('cy', y);

                            lineStart.setAttribute('visibility', 'visible');
                        } else if (i === 1) {
                            line.setAttribute('x2', x);
                            line.setAttribute('y2', y);
                            lineEnd.setAttribute('cx', x);
                            lineEnd.setAttribute('cy', y);
                            lineEnd.setAttribute('visibility', 'visible');
                        }
                    } else {
                        if (i === 0) {
                            lineStart.setAttribute('visibility', 'hidden');
                        } else {
                            lineEnd.setAttribute('visibility', 'hidden');
                        }
                    }
                }

                if (splat.measurePoints.length === 2) {
                    getPoint2d(0, screen0);
                    getPoint2d(1, screen1);
                    const x1 = screen0.x;
                    const y1 = screen0.y;
                    const x2 = screen1.x;
                    const y2 = screen1.y;
                    const mx = (x1 + x2) * 0.5;
                    const my = (y1 + y2) * 0.5;
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    let rotDeg = Math.atan2(dy, dx) * 180 / Math.PI;
                    // Keep text upright (same idea as Google Maps distance labels)
                    if (rotDeg > 90) rotDeg -= 180;
                    if (rotDeg < -90) rotDeg += 180;

                    getPoint(0, p0);
                    getPoint(1, p1);
                    const lenMeters = p0.distance(p1);
                    const displayVal =
                        lenMeters * globalMeasureCalibration * referenceScale * UNITS_TO_METERS_FACTOR[currentUnit];
                    const prec = precisionForUnit(currentUnit);
                    const labelStr = Number.isFinite(displayVal) ? `${displayVal.toFixed(prec)} ${currentUnit}` : '';
                    labelText.textContent = labelStr;
                    labelG.setAttribute('transform', `translate(${mx},${my}) rotate(${rotDeg})`);
                    labelG.setAttribute('visibility', labelStr ? 'visible' : 'hidden');

                    const padX = 6;
                    const padY = 3;
                    const fh = 16;
                    let tw = 0;
                    try {
                        tw = labelText.getComputedTextLength();
                    } catch {
                        tw = 0;
                    }
                    if (!Number.isFinite(tw) || tw <= 0) {
                        tw = Math.max(8, labelStr.length * 6.5);
                    }
                    labelBg.setAttribute('x', String(-(tw * 0.5 + padX)));
                    labelBg.setAttribute('y', String(-(fh * 0.5)));
                    labelBg.setAttribute('width', String(tw + padX * 2));
                    labelBg.setAttribute('height', String(fh));
                    labelBg.setAttribute('rx', '3');
                } else {
                    labelG.setAttribute('visibility', 'hidden');
                }
            } else {
                line.setAttribute('visibility', 'hidden');
                lineStart.setAttribute('visibility', 'hidden');
                lineEnd.setAttribute('visibility', 'hidden');
                labelG.setAttribute('visibility', 'hidden');
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        this.activate = () => {
            active = true;
            updateVisuals();
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            selectToolbar.hidden = false;
            parent.style.display = 'block';
            parent.classList.add('noevents');
            svg.classList.remove('hidden');

            events.fire('transformHandler.push', transformHandler);
        };

        this.deactivate = () => {
            active = false;
            referenceScale = 1;
            removeMeasureConfirmOverlay();
            closeUnitList();
            updateVisuals();
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup);
            selectToolbar.hidden = true;
            parent.style.display = 'none';
            parent.classList.remove('noevents');
            svg.classList.add('hidden');

            events.fire('transformHandler.pop');
        };
    }
}

export { MeasureTool };
